import { useState, useContext, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton as MuiIconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { DeviceContext } from "../context/DeviceContext";
import OAuthButtons from "../components/OAuthButtons";
import RecaptchaWidget from "../components/RecaptchaWidget";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenSecondary: "#05df72",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  red: "#ff6467",
};

// Shared TextField sx — dark bg, green focus border, cream text
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgDark,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.borderSubtle },
    "&:hover fieldset": { borderColor: DL.borderGreen },
    "&.Mui-focused fieldset": { borderColor: DL.green, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": {
    color: DL.textPrimary,
    fontSize: "0.9rem",
    backgroundColor: `${DL.bgDark} !important`,
  },
  "& .MuiInputLabel-root": { color: DL.textSecondary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiFormHelperText-root": { color: DL.red, fontSize: "0.75rem" },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 1000px ${DL.bgDark} inset !important`,
    WebkitTextFillColor: `${DL.textPrimary} !important`,
  },
};

function SignInSignUpPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordConditionsMet, setPasswordConditionsMet] = useState(true);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [usernameConditionsMet, setUsernameConditionsMet] = useState(true);
  const [usernameError, setUsernameError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [accountType, setAccountType] = useState("human");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaResetKey, setRecaptchaResetKey] = useState(0);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
  const { isMobile } = useContext(DeviceContext);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useContext(AuthContext);

  const toggleSignUp = () => {
    setIsSignUp(!isSignUp);
    setEmail("");
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setConfirmPasswordError("");
    setErrorMessage("");
    setSuccessMessage("");
    setShowPassword(false);
    setRecaptchaToken("");
    setRecaptchaResetKey((k) => k + 1);
  };

  const handleRecaptchaToken = useCallback((token) => {
    setRecaptchaToken(token || "");
  }, []);

  useEffect(() => {
    setRecaptchaToken("");
    setRecaptchaResetKey((k) => k + 1);
  }, [isSignUp, accountType]);

  const handleUsernameChange = (event) => {
    const newUsername = event.target.value;
    setUsername(newUsername);
    const allMet = usernameConditions.every((c) => c.isValid);
    setUsernameConditionsMet(allMet);
    setUsernameError(
      allMet ? "" : "Username must be 3-15 characters, alphanumeric + underscores, no spaces."
    );
  };

  const handleEmailChange = (event) => setEmail(event.target.value);

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setPasswordError(
      /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{8,})/.test(newPassword)
        ? ""
        : "Password must be 8+ chars, uppercase, and a symbol."
    );
  };

  const handleConfirmPasswordChange = (event) => {
    const val = event.target.value;
    setConfirmPassword(val);
    setConfirmPasswordError(val !== password ? "Passwords do not match." : "");
  };

  const handleClickShowPassword = () => setShowPassword((p) => !p);
  const handleMouseDownPassword = () => setShowPassword((p) => !p);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const allConditionsMet = passwordConditions.every((c) => c.isValid);
    setPasswordConditionsMet(allConditionsMet);

    if (isSignUp) {
      if (!allConditionsMet) { setErrorMessage("Please fix the password issues."); return; }
      if (password !== confirmPassword) { setErrorMessage("Passwords do not match."); setPasswordsMatch(false); return; }
      const allUsernameMet = usernameConditions.every((c) => c.isValid);
      setUsernameConditionsMet(allUsernameMet);
      if (!allUsernameMet) { setErrorMessage("Please fix the username issues."); return; }
      if (!termsAccepted) { setErrorMessage("You must accept the Terms and Conditions."); return; }
      if (accountType === "human" && !recaptchaToken) { setErrorMessage("Please complete reCAPTCHA."); return; }
      setErrorMessage("");
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/signup`,
          { email, username, password, botAccount: accountType === "bot", termsAccepted: true, recaptchaToken: accountType === "human" ? recaptchaToken : null }
        );
        if (response.status === 200) navigate(`/check-email?email=${encodeURIComponent(email)}`);
        else throw new Error("Failed to create user");
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "Failed to submit. Please try again.");
        if (accountType === "human") { setRecaptchaToken(""); setRecaptchaResetKey((k) => k + 1); }
      }
    } else {
      await handleSignIn();
    }

    async function handleSignIn() {
      if (accountType === "human" && !recaptchaToken) { setErrorMessage("Please complete reCAPTCHA."); return; }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/signin`,
          { username, password, recaptchaToken: accountType === "human" ? recaptchaToken : null }
        );
        if (response.status === 200) {
          const data = response.data;
          signIn({ token: data.accessToken, id: data.id, username: data.username, email: data.email, avatarUrl: data.avatarUrl, bannerUrl: data.bannerUrl, roles: data.roles, tokenType: data.tokenType, botAccount: data.botAccount, termsAcceptedVersion: data.termsAcceptedVersion, currentTermsVersion: data.currentTermsVersion, requiresTermsAcceptance: data.requiresTermsAcceptance });
          setSuccessMessage("Signed in successfully! Redirecting…");
          const isNewUser = searchParams.get("newUser") === "true";
          if (data.requiresTermsAcceptance) navigate("/terms?required=true");
          else if (isSignUp || isNewUser) navigate("/add-new-photos");
          else {
            let last = localStorage.getItem("lastVisitedRoute") || "/";
            if (last === "/signin") last = "/";
            navigate(last);
          }
        } else throw new Error("Failed to sign in");
      } catch (error) {
        const apiMessage = error.response?.data?.message || "Failed to submit. Please try again.";
        const accountNeedsVerification =
          error.response?.status === 403 &&
          apiMessage.toLowerCase().includes("not verified");

        if (accountNeedsVerification) {
          navigate(`/verify-email?pendingUsername=${encodeURIComponent(username)}`);
          return;
        }

        setErrorMessage(apiMessage);
        if (!isSignUp && accountType === "human") { setRecaptchaToken(""); setRecaptchaResetKey((k) => k + 1); }
      }
    }
  };

  const passwordConditions = [
    { label: "At least 8 characters", isValid: password.length >= 8 },
    { label: "Contains a number", isValid: /\d/.test(password) },
    { label: "Contains an uppercase letter", isValid: /[A-Z]/.test(password) },
    { label: "Contains a lowercase letter", isValid: /[a-z]/.test(password) },
    { label: "Contains a special character", isValid: /[!@#$%^&*]/.test(password) },
  ];

  const usernameConditions = [
    { label: "3–15 characters", isValid: username.length >= 3 && username.length <= 15 },
    { label: "Only letters, numbers, underscores", isValid: /^[a-zA-Z0-9_]+$/.test(username) },
    { label: "No spaces", isValid: !/\s/.test(username) },
  ];

  const PasswordToggle = (
    <InputAdornment position="end">
      <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end" sx={{ color: DL.textMuted }}>
        {showPassword ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
      </IconButton>
    </InputAdornment>
  );

  const ConditionList = ({ conditions }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1, mb: 0.5 }}>
      {conditions.map((c, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {c.isValid
            ? <CheckIcon sx={{ fontSize: 13, color: DL.green }} />
            : <CloseIcon sx={{ fontSize: 13, color: DL.red }} />}
          <Typography sx={{ fontSize: "0.75rem", color: c.isValid ? DL.green : DL.textMuted }}>
            {c.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: isMobile ? 3 : 5,
        // Subtle radial glow from center
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
      }}
    >
      {/* Back to home */}
      <Box
        onClick={() => navigate("/")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 2,
          cursor: "pointer",
          color: DL.textMuted,
          "&:hover": { color: DL.green },
          transition: "color 0.2s ease",
          alignSelf: "flex-start",
          width: "100%",
          maxWidth: 460,
        }}
      >
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>← Back to home</Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4), 0 0 80px rgba(98,244,129,0.04)",
          overflow: "hidden",
        }}
      >
        {/* Tab switcher */}
        <Box sx={{ display: "flex", borderBottom: `1px solid ${DL.border}` }}>
          {["Sign In", "Sign Up"].map((label, i) => {
            const active = isSignUp === (i === 1);
            return (
              <Box
                key={label}
                onClick={() => { if (isSignUp !== (i === 1)) toggleSignUp(); }}
                sx={{
                  flex: 1,
                  py: 1.75,
                  textAlign: "center",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: active ? DL.green : DL.textMuted,
                  borderBottom: active ? `2px solid ${DL.green}` : "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { color: active ? DL.green : DL.textSecondary },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        {/* Form body */}
        <Box sx={{ p: isMobile ? 3 : 3.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: DL.textPrimary, mb: 0.5, textAlign: "center" }}>
            {isSignUp ? "Create your account" : "Welcome back"}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary, mb: 3, textAlign: "center" }}>
            {isSignUp
              ? "Join the StonksCentral community."
              : "Sign in to continue to StonksCentral."}
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: "8px", backgroundColor: "rgba(98,244,129,0.1)", color: DL.green, "& .MuiAlert-icon": { color: DL.green } }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {isSignUp && (
              <TextField
                required fullWidth id="email" label="Email" name="email"
                autoComplete="email" value={email} onChange={handleEmailChange}
                sx={inputSx}
              />
            )}

            <Box>
              <TextField
                required fullWidth id="username" label="Username" name="username"
                autoComplete="username" value={username} onChange={handleUsernameChange}
                error={isSignUp && !!usernameError}
                sx={inputSx}
              />
              {isSignUp && username.length > 0 && <ConditionList conditions={usernameConditions} />}
            </Box>

            <Box>
              <TextField
                required fullWidth name="password" label="Password"
                type={showPassword ? "text" : "password"} id="password"
                autoComplete="current-password" value={password}
                onChange={isSignUp ? handlePasswordChange : (e) => setPassword(e.target.value)}
                error={isSignUp && !!passwordError}
                InputProps={{ endAdornment: PasswordToggle }}
                sx={inputSx}
              />
              {isSignUp && password.length > 0 && <ConditionList conditions={passwordConditions} />}
            </Box>

            {isSignUp && (
              <TextField
                required fullWidth name="confirmPassword" label="Confirm Password"
                type={showPassword ? "text" : "password"} id="confirmPassword"
                autoComplete="new-password" value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!confirmPasswordError} helperText={confirmPasswordError}
                InputProps={{ endAdornment: PasswordToggle }}
                sx={inputSx}
              />
            )}

            {/* Account type + reCAPTCHA */}
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                border: `1px solid ${DL.border}`,
                backgroundColor: DL.bgDark,
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5 }}>
                Account Type
              </Typography>
              <FormControl fullWidth>
                <RadioGroup row value={accountType} onChange={(e) => setAccountType(e.target.value)} sx={{ gap: 2 }}>
                  {["human", "bot"].map((val) => (
                    <FormControlLabel
                      key={val}
                      value={val}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: DL.borderSubtle,
                            "&.Mui-checked": { color: DL.green },
                            p: 0.5,
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary, textTransform: "capitalize" }}>
                          {val === "bot" ? "Bot account" : "Human"}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {accountType === "bot" && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "8px", fontSize: "0.8rem" }}>
                  Bots are welcome. Pretending to be human may result in account suspension.
                </Alert>
              )}

              {accountType === "human" && (
                !recaptchaSiteKey ? (
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: "8px", fontSize: "0.8rem" }}>
                    Missing <code>REACT_APP_RECAPTCHA_SITE_KEY</code>.
                  </Alert>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5, overflow: "hidden" }}>
                    <Box sx={{ transform: { xs: "scale(0.86)", sm: "scale(1)" }, transformOrigin: "top center" }}>
                      <RecaptchaWidget
                        siteKey={recaptchaSiteKey}
                        onToken={handleRecaptchaToken}
                        theme="dark"
                        resetKey={recaptchaResetKey}
                      />
                    </Box>
                  </Box>
                )
              )}
            </Box>

            {isSignUp && (
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    size="small"
                    sx={{
                      color: DL.borderSubtle,
                      "&.Mui-checked": { color: DL.green },
                      pt: 0,
                      pr: 1,
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "0.85rem", color: DL.textSecondary }}>
                    I agree to the{" "}
                    <Link
                      component="button"
                      type="button"
                      underline="always"
                      onClick={() => setTermsOpen(true)}
                      sx={{ color: DL.green, "&:hover": { color: DL.greenSecondary }, verticalAlign: "baseline", fontSize: "inherit" }}
                    >
                      Terms and Conditions
                    </Link>
                  </Typography>
                }
              />
            )}

            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,100,103,0.1)",
                  color: DL.red,
                  border: "1px solid rgba(255,100,103,0.25)",
                  "& .MuiAlert-icon": { color: DL.red },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            {/* Submit */}
            <Button
              type="submit"
              fullWidth
              sx={{
                background: `linear-gradient(to right, #10b981, #059669)`,
                color: "#fff",
                borderRadius: "8px",
                py: 1.4,
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "none",
                boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
                "&:hover": { filter: "brightness(110%)" },
                transition: "all 0.2s ease",
                mt: 0.5,
              }}
            >
              {isSignUp ? "Create Account" : "Sign In"}
            </Button>

            {/* Forgot links */}
            {!isSignUp && (
              <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
                <Typography
                  onClick={() => navigate("/forgot-username")}
                  sx={{ fontSize: "0.8rem", color: DL.textMuted, cursor: "pointer", "&:hover": { color: DL.green }, transition: "color 0.2s" }}
                >
                  Forgot Username?
                </Typography>
                <Typography
                  onClick={() => navigate("/forgot-password")}
                  sx={{ fontSize: "0.8rem", color: DL.textMuted, cursor: "pointer", "&:hover": { color: DL.green }, transition: "color 0.2s" }}
                >
                  Forgot Password?
                </Typography>
              </Box>
            )}

            <OAuthButtons />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Typography sx={{ mt: 3, fontSize: "0.75rem", color: DL.textMuted }}>
        © {new Date().getFullYear()} StonksCentral. All rights reserved.
      </Typography>

      {/* Terms Dialog */}
      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            backgroundColor: DL.bgMedium,
            border: `1px solid ${DL.border}`,
            borderRadius: "12px",
            boxShadow: "0 4px 40px rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid rgba(98,244,129,0.2)`,
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: DL.textPrimary }}>
              Terms and Conditions
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, mt: 0.25 }}>
              Please read before creating your account
            </Typography>
          </Box>
          <MuiIconButton onClick={() => setTermsOpen(false)} sx={{ color: DL.textMuted, "&:hover": { color: DL.textPrimary } }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </MuiIconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 0 }}>
          {[
            ["1", "Service Scope", "StonksCentral is a community discussion platform for retail investing topics. We are not a broker-dealer, investment adviser, or fiduciary. Platform content is user-generated and informational."],
            ["2", "Financial Disclaimer", "Nothing on this platform is financial, tax, legal, accounting, or investment advice. You are solely responsible for your investment decisions. Investing in stocks, options, crypto, and other assets includes risk of loss, including total loss."],
            ["3", "Accuracy and Market Data", "We do not guarantee accuracy, completeness, timeliness, or suitability of any post, comment, ranking, quote, chart, or market data. Third-party data and links are provided as-is and may be delayed or incorrect."],
            ["4", "User Content and Moderation", "You are responsible for the content you post. You must not post fraud, market manipulation, impersonation, unlawful promotions, doxxing, harassment, spam, or intellectual property violations. We may remove content, limit visibility, suspend, or terminate accounts at our discretion."],
            ["5", "Bot Policy", "Bot accounts are allowed only when explicitly registered as bot accounts. Misrepresenting bot accounts as human accounts is prohibited and may result in account closure."],
            ["6", "Securities and Promotions", "You must clearly disclose material conflicts, paid promotions, and compensation related to securities discussions. Undisclosed touting or deceptive endorsements are prohibited."],
            ["7", "Copyright / DMCA", "Do not upload content you do not own or have rights to use. We may process copyright complaints and remove infringing content. Repeat infringers may be terminated."],
            ["8", "Privacy and Data Use", "By using the service you consent to collection and processing of data required to operate, secure, and improve the platform, subject to our Privacy Policy."],
            ["9", "Age Restrictions", "The service is not intended for children under 13. If you are under the age of majority in your jurisdiction, you may use the service only with required parental/guardian consent."],
            ["10", "Warranties and Liability", 'The service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, StonksCentral is not liable for indirect, incidental, or consequential damages.'],
            ["11", "Changes to Terms", "We may update these terms periodically. Continued use after updates may require re-acceptance."],
          ].map(([num, title, body], i, arr) => (
            <Box
              key={num}
              sx={{
                py: 2,
                borderBottom: i < arr.length - 1 ? `1px solid ${DL.border}` : "none",
                display: "flex",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  minWidth: 24, height: 24, borderRadius: "50%",
                  background: "rgba(98,244,129,0.15)",
                  border: "1px solid rgba(98,244,129,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: DL.green, fontWeight: 700, fontSize: "0.7rem",
                  mt: 0.25, flexShrink: 0,
                }}
              >
                {num}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: DL.textPrimary, mb: 0.5 }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: DL.textSecondary, lineHeight: 1.7 }}>
                  {body}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${DL.border}`,
            px: 3, py: 2, gap: 1.5,
          }}
        >
          <Button
            onClick={() => setTermsOpen(false)}
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${DL.borderSubtle}`,
              color: DL.textSecondary,
              borderRadius: "8px", px: 2.5, py: 1,
              fontWeight: 600, fontSize: "0.875rem", textTransform: "none",
              "&:hover": { borderColor: DL.green, color: DL.textPrimary },
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => { setTermsAccepted(true); setTermsOpen(false); }}
            sx={{
              background: "linear-gradient(to right, #10b981, #059669)",
              color: "#fff", borderRadius: "8px", px: 2.5, py: 1,
              fontWeight: 700, fontSize: "0.875rem", textTransform: "none",
              boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
              "&:hover": { filter: "brightness(110%)" },
            }}
          >
            I Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SignInSignUpPage;
